/**
 * @file: ContactRow.tsx
 *
 * One selectable contact row in the picker (checkbox + avatar + name/title),
 * plus the thin list separator. Presentational only.
 */

import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatContact } from '@euler/model/chat';
import { Check } from 'lucide-react-native';
import { FC, memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

export const ContactRow: FC<{
  contact: ChatContact;
  checked?: boolean;
  locked?: boolean;
  /** Show the multi-select checkbox. Direct (single-tap) mode hides it. */
  showCheck?: boolean;
  onPress: () => void;
}> = memo(
  ({ contact, checked = false, locked = false, showCheck = true, onPress }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        activeOpacity={locked ? 1 : 0.7}
        onPress={onPress}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(12),
          gap: rpx(12),
          opacity: locked ? 0.7 : 1,
        }}
      >
        {showCheck ? (
          <View
            style={{
              width: rpx(20),
              height: rpx(20),
              borderRadius: rpx(10),
              borderWidth: rpx(1.5),
              borderColor: checked ? '#3b82f6' : '#d1d5db',
              backgroundColor: checked ? '#3b82f6' : 'transparent',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {checked ? (
              <Check size={rpx(14)} color="#fff" strokeWidth={3} />
            ) : null}
          </View>
        ) : null}
        <Avatar
          uri={contact.avatar ?? undefined}
          name={contact.name}
          textStyle={{
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(14),
            lineHeight: rpx(20),
          }}
          style={{
            width: rpx(36),
            height: rpx(36),
            borderRadius: rpx(18),
          }}
        />
        <View style={{ flex: 1 }}>
          <Typography.Text
            size={rpx(14)}
            color="#111"
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            {contact.name}
            {locked ? (
              <Typography.Text size={rpx(11)} color="#9ca3af">
                {'  '}已包含
              </Typography.Text>
            ) : null}
          </Typography.Text>
          {contact.jobTitle ? (
            <Typography.Text size={rpx(11)} color="#9ca3af">
              {contact.jobTitle}
            </Typography.Text>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  },
);

export const ContactRowSeparator = memo(() => (
  <View
    style={{
      height: StyleSheet.hairlineWidth,
      backgroundColor: '#f3f4f6',
      marginLeft: 80,
    }}
  />
));
